import '../styles/ExportDataPage.css'; // We'll create this CSS file later if needed

import React, {
  useRef,
  useState,
} from 'react';

import {
  collection,
  getDocs,
  getFirestore,
  query,
  where,
} from 'firebase/firestore';
import {
  useNavigate,
  useParams,
} from 'react-router-dom';

import firebase from '../firebase';
import { sortAthletesByNumber } from '../helper';
import { Athlete } from '../types/types';
import {
  Button,
  Card,
  Container,
  Typography,
} from './common';

export default function ExportDataPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const db = getFirestore(firebase);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [exportData, setExportData] = useState<string | null>(null);
  const [exportTitle, setExportTitle] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const exportContentRef = useRef<HTMLPreElement>(null);

  const handleExport = async (teamPlacement: 'coed' | 'allgirl' | 'level4' | 'yes' | 'maybe' | 'no') => {
    if (!sessionId) {
      setError('Session ID is missing.');
      return;
    }
    setLoading(true);
    setError('');
    setCopySuccess(false);

    try {
      const athletesRef = collection(db, `sessions/${sessionId}/athletes`);
      const q = query(
        athletesRef,
        where('teamplacement', '==', teamPlacement)
      );
      const querySnapshot = await getDocs(q);

      const athletesList: Athlete[] = [];
      querySnapshot.forEach(doc => {
        athletesList.push({ id: doc.id, ...doc.data() } as Athlete);
      });

      // Sort athletes by number in ascending order
      const sortedAthletes = sortAthletesByNumber(athletesList);

      if (sortedAthletes.length === 0) {
        alert(`No athletes found with placement: ${teamPlacement}`);
        setLoading(false);
        return;
      }

      const data = sortedAthletes.map(athlete =>
        `#${athlete.number}`
      ).join('\n');

      // Set the export data and title for display in the UI
      setExportData(data);
      setExportTitle(`${teamPlacement.charAt(0).toUpperCase() + teamPlacement.slice(1)} Team Export`);

    } catch (err) {
      console.error('Error exporting data:', err);
      setError('Failed to export data. See console for details.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyToClipboard = async () => {
    if (!exportData) return;
    
    try {
      if (navigator.clipboard && window.isSecureContext) {
        // Use the clipboard API when available
        await navigator.clipboard.writeText(exportData);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } else {
        // Fallback for browsers without clipboard API
        if (exportContentRef.current) {
          const range = document.createRange();
          range.selectNode(exportContentRef.current);
          window.getSelection()?.removeAllRanges();
          window.getSelection()?.addRange(range);
          document.execCommand('copy');
          window.getSelection()?.removeAllRanges();
          setCopySuccess(true);
          setTimeout(() => setCopySuccess(false), 2000);
        }
      }
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleReset = () => {
    setExportData(null);
    setExportTitle('');
  };

  return (
    <Container>
      <Card>
        <Typography variant="h2">Export Athlete Data</Typography>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="export-actions">
          <Button
            variant="primary"
            onClick={() => handleExport('coed')}
            disabled={loading}
            fullWidth
          >
            {loading ? 'Exporting...' : 'Export coed'}
          </Button>
          <Button
            variant="primary"
            onClick={() => handleExport('allgirl')}
            disabled={loading}
            fullWidth
          >
            {loading ? 'Exporting...' : 'Export allgirl'}
          </Button>
          <Button
            variant="primary"
            onClick={() => handleExport('level4')}
            disabled={loading}
            fullWidth
          >
            {loading ? 'Exporting...' : 'Export level4'}
          </Button>
          <Button
            variant="primary"
            onClick={() => handleExport('yes')}
            disabled={loading}
            fullWidth
          >
            {loading ? 'Exporting...' : 'Export yes?'}
          </Button>
          <Button
            variant="primary"
            onClick={() => handleExport('maybe')}
            disabled={loading}
            fullWidth
          >
            {loading ? 'Exporting...' : 'Export maybe'}
          </Button>
          <Button
            variant="primary"
            onClick={() => handleExport('no')}
            disabled={loading}
            fullWidth
          >
            {loading ? 'Exporting...' : 'Export no'}
          </Button>
        </div>

        <div className="actions" style={{ marginTop: '30px' }}>
          <Button
            variant="secondary"
            fullWidth
            onClick={() => navigate(`/session/${sessionId}`)}
          >
            Back
          </Button>
        </div>
      </Card>

      {exportData && (
        <Card className="export-result-card">
          <div className="export-header">
            <Typography variant="h2">{exportTitle}</Typography>
            <div className="export-buttons">
              <Button
                variant="primary"
                onClick={handleCopyToClipboard}
                disabled={!exportData}
              >
                {copySuccess ? 'Copied!' : 'Copy'}
              </Button>
              <Button
                variant="secondary"
                onClick={handleReset}
              >
                Close
              </Button>
            </div>
          </div>
          
          <pre 
            ref={exportContentRef}
            className="export-content"
          >
            {exportData}
          </pre>
        </Card>
      )}
    </Container>
  );
} 